import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Edit, ChevronUp, ChevronDown, ClipboardList } from "lucide-react";

function FunProfilePanel() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [reordering, setReordering] = useState(false);
  const blank = { section: "More About You", icon: "✨", label: "", type: "radio", options: ["", ""], allow_multiple: false, allow_custom: false };

  const refresh = () => api.get("/admin/profile-questions").then((r) => setList(r.data));
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    const body = { ...editing };
    if (!body.label.trim()) { toast.error("Question text is required"); return; }
    if (body.type === "radio") {
      body.options = (body.options || []).map((o) => o.trim()).filter(Boolean);
      if (body.options.length < 2) { toast.error("Add at least 2 options"); return; }
    }
    try {
      if (body.id) await api.put(`/admin/profile-questions/${body.id}`, body);
      else await api.post("/admin/profile-questions", body);
      toast.success(body.id ? "Question updated" : "Question added");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this profile question?")) return;
    await api.delete(`/admin/profile-questions/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const move = async (index, dir) => {
    const next = [...list];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setList(next);
    setReordering(true);
    try {
      await api.put("/admin/profile-questions/reorder", { ids: next.map((q) => q.id) });
    } catch {
      toast.error("Reorder failed");
      refresh();
    } finally { setReordering(false); }
  };

  return (
    <div className="space-y-5">
      <div className="brutal-card p-5">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h3 className="font-black uppercase flex items-center gap-2"><ClipboardList size={18}/> Fun Profile Questions</h3>
            <p className="text-xs text-neutral-600 mt-1 max-w-2xl">
              These are the questions new employees answer in the welcome form. Add, delete or use the ▲▼ arrows to reorder them.
              Turn on <strong>Allow multiple responses</strong> on any question so employees can pick more than one option.
            </p>
          </div>
          <button data-testid="funq-new-btn" onClick={() => setEditing(blank)} className="brutal-btn inline-flex items-center gap-1"><Plus size={14}/> New Question</button>
        </div>
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-neutral-600">No questions yet — click “New Question” to add one.</p>
      ) : (
        <div className="grid gap-2">
          {list.map((q, idx) => (
            <div key={q.id} data-testid={`funq-row-${q.id}`} className="border-2 border-black p-3 flex items-start gap-3 bg-white">
              <div className="flex flex-col gap-1 pt-0.5">
                <button data-testid={`funq-up-${q.id}`} disabled={idx === 0 || reordering} onClick={() => move(idx, -1)} title="Move up" className="p-0.5 border-2 border-black bg-white hover:bg-[#FFD600] disabled:opacity-30"><ChevronUp size={14}/></button>
                <button data-testid={`funq-down-${q.id}`} disabled={idx === list.length - 1 || reordering} onClick={() => move(idx, 1)} title="Move down" className="p-0.5 border-2 border-black bg-white hover:bg-[#FFD600] disabled:opacity-30"><ChevronDown size={14}/></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs uppercase font-mono-bq mb-1 flex-wrap">
                  <span className="border border-black px-1.5 py-0.5">#{idx + 1}</span>
                  <span className="border border-black px-1.5 py-0.5">{q.icon} {q.section}</span>
                  <span className="border border-black px-1.5 py-0.5">{q.type === "text" ? "TEXT" : "CHOICE"}</span>
                  {q.allow_multiple && <span className="border border-black px-1.5 py-0.5 bg-[#39FF14]">MULTI-SELECT</span>}
                  {q.allow_custom && <span className="border border-black px-1.5 py-0.5 bg-[#FFD600]">CUSTOM OK</span>}
                </div>
                <div className="font-black">{q.label}</div>
                {q.type === "radio" && (
                  <div className="text-xs text-neutral-600 mt-1 truncate">{q.options.join(" · ")}</div>
                )}
              </div>
              <button onClick={() => setEditing(q)} data-testid={`funq-edit-${q.id}`} className="brutal-btn text-xs"><Edit size={12}/></button>
              <button onClick={() => remove(q.id)} data-testid={`funq-del-${q.id}`} className="brutal-btn text-xs"><Trash2 size={12}/></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="border-4 border-black rounded-none max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="uppercase font-black">{editing?.id ? "Edit" : "New"} Profile Question</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs uppercase font-black">Section</label>
                  <input data-testid="funq-form-section" value={editing.section} onChange={(e) => setEditing({ ...editing, section: e.target.value })} className="border-2 border-black p-2 w-full" placeholder="e.g. Gaming Corner"/>
                </div>
                <div>
                  <label className="block text-xs uppercase font-black">Icon/Emoji</label>
                  <input data-testid="funq-form-icon" value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} className="border-2 border-black p-2 w-full" placeholder="🎮"/>
                </div>
              </div>

              <label className="block text-xs uppercase font-black">Question</label>
              <textarea data-testid="funq-form-label" value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} className="border-2 border-black p-2 w-full" rows={2} placeholder="What's your favorite game?"/>

              <label className="block text-xs uppercase font-black">Answer type</label>
              <select data-testid="funq-form-type" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="border-2 border-black p-2 w-full font-bold">
                <option value="radio">Multiple choice (options)</option>
                <option value="text">Free text</option>
              </select>

              {editing.type === "radio" && (
                <>
                  <div className="flex items-center justify-between border-2 border-black p-3 bg-[#FFFCE5]">
                    <div>
                      <div className="font-black uppercase text-sm">Allow multiple responses</div>
                      <div className="text-xs text-neutral-600">Let employees select more than one option for this question.</div>
                    </div>
                    <Switch data-testid="funq-form-multiple-switch" checked={!!editing.allow_multiple} onCheckedChange={(v) => setEditing({ ...editing, allow_multiple: v })}/>
                  </div>
                  <div className="flex items-center justify-between border-2 border-black p-3">
                    <div>
                      <div className="font-black uppercase text-sm">Allow “Other / Custom”</div>
                      <div className="text-xs text-neutral-600">Adds a free-text option for answers not in the list (single-choice only).</div>
                    </div>
                    <Switch data-testid="funq-form-custom-switch" checked={!!editing.allow_custom} disabled={editing.allow_multiple} onCheckedChange={(v) => setEditing({ ...editing, allow_custom: v })}/>
                  </div>

                  <label className="block text-xs uppercase font-black">Options</label>
                  {(editing.options || []).map((opt, i) => (
                    <div key={`funq-opt-${i}`} className="flex items-center gap-2">
                      <span className="font-black w-6">{i + 1}.</span>
                      <input
                        data-testid={`funq-form-option-${i}`}
                        value={opt}
                        onChange={(e) => { const next = [...editing.options]; next[i] = e.target.value; setEditing({ ...editing, options: next }); }}
                        className="border-2 border-black p-2 flex-1"
                        placeholder={`Option ${i + 1}`}
                      />
                      {editing.options.length > 2 && (
                        <button data-testid={`funq-form-remove-option-${i}`} onClick={() => setEditing({ ...editing, options: editing.options.filter((_, j) => j !== i) })} className="p-1 border-2 border-black hover:bg-[#FF3B30] hover:text-white" title="Remove option"><Trash2 size={12}/></button>
                      )}
                    </div>
                  ))}
                  <button data-testid="funq-form-add-option" onClick={() => setEditing({ ...editing, options: [...(editing.options || []), ""] })} className="brutal-btn-secondary text-xs inline-flex items-center gap-1"><Plus size={12}/> Add option</button>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="brutal-btn-secondary">Cancel</button>
            <button data-testid="funq-form-save" onClick={save} className="brutal-btn">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FunProfilePanel;
